import React, {useState, useEffect, useRef, useCallback} from 'react'
import {Prompt} from '@modifi/router'
import type {Location} from 'history'

type ExitPromptDialogData = {
  handleConfirm: () => void
  handleCancel: () => void
  canSubmit: boolean
  isFinalStep: boolean
}

type NavigationHookProps = {
  showDialog: (data: ExitPromptDialogData) => void
  closeDialog: () => void
  onConfirm: () => void | Promise<void>
  onCancel: () => void | Promise<void>
  redirect: (location: Location) => void
  navigationLocked: boolean
  canSubmit: boolean
  loading?: boolean
  hasError?: boolean
  updateDialogData: (data: {loading?: boolean}) => void
  isFinalStep?: boolean
}

const NavigationHook: React.FC<NavigationHookProps> = ({
  showDialog,
  closeDialog,
  onConfirm,
  onCancel,
  redirect,
  navigationLocked,
  canSubmit,
  loading = false,
  hasError = false,
  updateDialogData,
  isFinalStep = true,
}) => {
  const [confirmed, setConfirmed] = useState(false)
  const [cancelled, setCancelled] = useState(false)
  const [lastLocation, setLastLocation] = useState<Location | null>(null)

  const prevLoadingRef = useRef(loading)
  useEffect(() => {
    if (confirmed && prevLoadingRef.current && !loading) {
      closeDialog()
      !hasError && lastLocation && redirect(lastLocation)
      setConfirmed(false)
    }
    prevLoadingRef.current = loading
  }, [closeDialog, confirmed, lastLocation, redirect, loading, hasError])

  // Some forms' submit actions navigate away on success, unmounting this
  // component before the loading-transition effect above can fire. On unmount
  // with a pending confirm, close the dialog and redirect to the user's
  // intended destination.
  const confirmedRef = useRef(confirmed)
  const lastLocationRef = useRef(lastLocation)
  const hasErrorRef = useRef(hasError)
  useEffect(() => {
    confirmedRef.current = confirmed
    lastLocationRef.current = lastLocation
    hasErrorRef.current = hasError
  })
  useEffect(
    () => () => {
      if (confirmedRef.current) {
        closeDialog()
        if (!hasErrorRef.current && lastLocationRef.current) {
          redirect(lastLocationRef.current)
        }
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  )

  useEffect(() => {
    updateDialogData({loading})
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading])

  const prevCancelledRef = useRef(cancelled)
  useEffect(() => {
    if (cancelled && cancelled !== prevCancelledRef.current) {
      closeDialog()
      lastLocation && redirect(lastLocation)
      setCancelled(false)
    }
    prevCancelledRef.current = cancelled
  }, [cancelled, closeDialog, lastLocation, onCancel, redirect])

  const handleConfirm = useCallback(async () => {
    if (!isFinalStep) {
      closeDialog()
    } else {
      await onConfirm()
    }

    setConfirmed(true)
  }, [onConfirm, closeDialog, isFinalStep])

  const handleCancel = useCallback(async () => {
    await onCancel()
    setCancelled(true)
  }, [onCancel])

  return (
    <Prompt
      when={!confirmed && !cancelled && navigationLocked}
      message={(nextLocation: Location) => {
        if (!confirmed) {
          if (nextLocation) {
            setLastLocation(nextLocation)
          }
          showDialog({
            // Since dialog does not re-subscribe to the latest state & reuses the first
            // object reference. Hence, below loading variable would not even work being stale
            // to be fixed later, maybe by using a redux state for exitPromptDialog and updating it's
            // value from this component
            // loading,
            handleConfirm,
            handleCancel,
            canSubmit: canSubmit && !confirmed,
            isFinalStep,
          })
          return false
        }
        return true
      }}
    />
  )
}

export default NavigationHook
